import type { BoardTemplate } from '../types/template';

/**
 * 보드 템플릿 프리셋. 문구는 i18n 키(boardTemplates ns)로 참조 — ko/en 원문은 시트/locale 관리.
 * emoji = native unicode. 렌더는 t(nameKey)/t(descriptionKey), 생성 시 t()로 평문 해석.
 */
export const BOARD_EMOJI_CANDIDATES = [
  '📌',
  '📝',
  '💡',
  '✅',
  '⭐',
  '📚',
  '🎯',
  '🔎',
  '💬',
  '🏫',
] as const;

export const DEFAULT_BOARD_EMOJI = '📝';

export const getRandomBoardEmoji = () =>
  BOARD_EMOJI_CANDIDATES[
    Math.floor(Math.random() * BOARD_EMOJI_CANDIDATES.length)
  ] ?? DEFAULT_BOARD_EMOJI;

/**
 * 순서 = 실사용 빈도·범용성 높은 순(운영 → 범용 → 관계 → 구조화 활동).
 * descriptionKey = 선생님이 템플릿을 고를 때 보는 소개, noticeKey = 학생에게 보이는 안내문.
 * emoji = 템플릿 테마에 맞춰 자유 선택(BOARD_EMOJI_CANDIDATES 풀과 무관 — 그건 새 빈 보드용).
 * 단, 피부색/ZWJ 조합 emoji는 쓰지 않고 macOS/Windows 모두 안정적으로 렌더되는 기본 emoji만 사용.
 * 각 템플릿 자체 groups emoji와도 겹치지 않게 선택.
 */
export const BOARD_TEMPLATES: BoardTemplate[] = [
  {
    id: 'class-notice',
    type: 'WALL',
    nameKey: 'classNotice.name',
    emoji: '🔔',
    thumbnail: '/assets/board-template-class-notice.webp',
    descriptionKey: 'classNotice.description',
    groups: [],
    noticeKey: 'classNotice.notice',
  },
  {
    id: 'idea-sharing',
    type: 'WALL',
    nameKey: 'ideaSharing.name',
    emoji: '💭',
    thumbnail: '/assets/board-template-idea-sharing.webp',
    descriptionKey: 'ideaSharing.description',
    groups: [],
    noticeKey: 'ideaSharing.notice',
  },
  {
    id: 'showcase',
    type: 'WALL',
    nameKey: 'showcase.name',
    emoji: '🖼️',
    thumbnail: '/assets/board-template-showcase.webp',
    descriptionKey: 'showcase.description',
    groups: [],
    noticeKey: 'showcase.notice',
  },
  {
    id: 'self-intro',
    type: 'WALL',
    nameKey: 'selfIntro.name',
    emoji: '👋',
    thumbnail: '/assets/board-template-self-intro.webp',
    descriptionKey: 'selfIntro.description',
    groups: [],
    noticeKey: 'selfIntro.notice',
  },
  {
    id: 'praise-board',
    type: 'WALL',
    nameKey: 'praiseBoard.name',
    emoji: '👏',
    thumbnail: '/assets/board-template-praise-board.webp',
    descriptionKey: 'praiseBoard.description',
    groups: [],
    noticeKey: 'praiseBoard.notice',
  },
  {
    id: 'birthday-board',
    type: 'WALL',
    nameKey: 'birthdayBoard.name',
    emoji: '🎂',
    thumbnail: '/assets/board-template-birthday-board.webp',
    descriptionKey: 'birthdayBoard.description',
    groups: [],
    noticeKey: 'birthdayBoard.notice',
  },
  {
    id: 'debate',
    type: 'GROUP',
    nameKey: 'debate.name',
    emoji: '⚖️',
    thumbnail: '/assets/board-template-debate.webp',
    descriptionKey: 'debate.description',
    groups: [
      { nameKey: 'debate.group.agree', emoji: '👍' },
      { nameKey: 'debate.group.disagree', emoji: '👎' },
      { nameKey: 'debate.group.undecided', emoji: '🤔' },
    ],
    noticeKey: 'debate.notice',
  },
  {
    id: 'lesson-reflection',
    type: 'GROUP',
    nameKey: 'lessonReflection.name',
    emoji: '📖',
    thumbnail: '/assets/board-template-lesson-reflection.webp',
    descriptionKey: 'lessonReflection.description',
    groups: [
      { nameKey: 'lessonReflection.group.learned', emoji: '💡' },
      { nameKey: 'lessonReflection.group.wonder', emoji: '❓' },
      { nameKey: 'lessonReflection.group.explore', emoji: '🔎' },
    ],
    noticeKey: 'lessonReflection.notice',
  },
  {
    id: 'kwl',
    type: 'GROUP',
    nameKey: 'kwl.name',
    emoji: '🗂️',
    thumbnail: '/assets/board-template-kwl.webp',
    descriptionKey: 'kwl.description',
    groups: [
      { nameKey: 'kwl.group.know', emoji: '🧠' },
      { nameKey: 'kwl.group.want', emoji: '❓' },
      { nameKey: 'kwl.group.learned', emoji: '✅' },
    ],
    noticeKey: 'kwl.notice',
  },
  {
    id: 'traffic-light',
    type: 'GROUP',
    nameKey: 'trafficLight.name',
    emoji: '🚦',
    thumbnail: '/assets/board-template-traffic-light.webp',
    descriptionKey: 'trafficLight.description',
    groups: [
      { nameKey: 'trafficLight.group.clear', emoji: '🟢' },
      { nameKey: 'trafficLight.group.confused', emoji: '🟡' },
      { nameKey: 'trafficLight.group.help', emoji: '🔴' },
    ],
    noticeKey: 'trafficLight.notice',
  },
];
